import { reasonSchema } from '@fruuit/shared'
import { Button, type ButtonVariant, Callout, Dialog, DialogCancel, Textarea } from '@fruuit/ui'
import { type FormEvent, type ReactNode, useId, useState } from 'react'
import { ApiError, errorMessage } from '../../api/client'

/** The subset of a zod schema used for client-side checks (the server re-validates). */
export interface StringSchema {
  safeParse(value: unknown): { success: boolean; error?: { issues: { message: string }[] } }
}

/** First validation message for `value`, or null when valid. */
export function schemaMessage(schema: StringSchema, value: string): string | null {
  const result = schema.safeParse(value)
  if (result.success) return null
  return result.error?.issues[0]?.message ?? 'This value is not valid.'
}

export interface ReasonDialogProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  /** Context above the reason field (what changes, who is affected). */
  children?: ReactNode
  label?: string
  hint?: ReactNode
  confirmLabel: string
  confirmVariant?: ButtonVariant
  /** Shared zod schema for the reason (defaults to the 8–12,000 character rule, WF-006). */
  schema?: StringSchema
  /** Optional reasons (e.g. a promotion note) may be left empty. */
  optional?: boolean
  /** Runs the command. Throw to keep the dialog open with the error shown. */
  onConfirm: (reason: string) => Promise<unknown>
}

/**
 * One dialog for every reasoned admin or triage action: subscribe, revoke, grant, lift,
 * dismiss, promote. Submit is single-flight; server field errors land on the reason field.
 */
export function ReasonDialog({
  open,
  onClose,
  title,
  description,
  children,
  label = 'Reason',
  hint = 'At least 8 characters. Recorded in the audit trail.',
  confirmLabel,
  confirmVariant = 'primary',
  schema = reasonSchema,
  optional = false,
  onConfirm,
}: ReasonDialogProps) {
  const formId = useId()
  const [reason, setReason] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    const message = optional && reason.trim() === '' ? null : schemaMessage(schema, reason)
    setFieldError(message)
    setFormError(null)
    if (message) return
    setBusy(true)
    try {
      await onConfirm(reason.trim())
    } catch (error) {
      const field =
        error instanceof ApiError ? (error.fieldErrors.reason ?? error.fieldErrors.note) : undefined
      if (field) setFieldError(field)
      else setFormError(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form={formId} variant={confirmVariant} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {children}
        <Textarea
          label={label}
          hint={hint}
          required={!optional}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          error={fieldError ?? undefined}
          rows={4}
          maxLength={12000}
        />
        {formError ? (
          <Callout tone="danger" role="alert">
            {formError}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  )
}
